import type { PatientBenefit, PatientDetail } from "@allergy123/contracts/clinical";
import { Button, Card, Field, Select, Status } from "@allergy123/ui";
import { useRef, useState } from "react";
import { useClinical, useLoad } from "../api";
import { errorText } from "../lib/errors";
import { SEX_LABELS, formatDateTime } from "../lib/format";
import { useSession } from "../session";

const RELATIONSHIPS = ["self", "spouse", "child", "other"] as const;
const BENEFIT_KINDS = ["test", "immunotherapy", "visit"] as const;
const BENEFIT_LABELS: Record<string, string> = { test: "Testing", immunotherapy: "Immunotherapy", visit: "Office visits" };

const dollars = (cents: number | null | undefined) => (cents === null || cents === undefined ? "" : (cents / 100).toFixed(2));
const cents = (text: string): number | null => (text.trim() === "" ? null : Math.round(Number(text) * 100));
const orNull = (text: string) => (text.trim() === "" ? null : text.trim());

function useSave(onSaved: () => void) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const run = async (fn: () => Promise<unknown>, done?: () => void) => {
    setBusy(true);
    setNote(null);
    try {
      await fn();
      setNote({ kind: "success", text: "Saved." });
      done?.();
      onSaved();
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  return { busy, note, run };
}

/** Demographics and contact details, with asthma status as a patient attribute with history (C-45). */
export function DemographicsCard({ p, onSaved }: { p: PatientDetail; onSaved: () => void }) {
  const s = useSession();
  const api = useClinical();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ givenName: p.givenName, familyName: p.familyName, dateOfBirth: p.dateOfBirth, sex: p.sex, address1: p.contact.address1 ?? "", address2: p.contact.address2 ?? "", city: p.contact.city ?? "", state: p.contact.state ?? "", postalCode: p.contact.postalCode ?? "", phone: p.contact.phone ?? "", email: p.contact.email ?? "", asthma: p.asthmaStatus ? (p.asthmaStatus.asthmatic ? "yes" : "no") : "unknown", asthmaFrom: p.asthmaStatus?.effectiveFrom ?? "" });
  const { busy, note, run } = useSave(onSaved);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });
  const address = [p.contact.address1, p.contact.address2, [p.contact.city, p.contact.state, p.contact.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  return (
    <Card as="section" aria-labelledby="demographics" dense>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 id="demographics" className="text-h3 font-semibold text-anchor">
          Details
        </h2>
        {s.can("clinical:write") && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>
      {!editing ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-body">
          <dt className="text-ink-secondary">Phone</dt>
          <dd>{p.contact.phone ?? "—"}</dd>
          <dt className="text-ink-secondary">Email</dt>
          <dd>{p.contact.email ?? "—"}</dd>
          <dt className="text-ink-secondary">Address</dt>
          <dd>{address || "—"}</dd>
          <dt className="text-ink-secondary">Asthma</dt>
          <dd>{p.asthmaStatus ? `${p.asthmaStatus.asthmatic ? "Asthmatic" : "Not asthmatic"} since ${p.asthmaStatus.effectiveFrom}` : "Not recorded"}</dd>
          <dt className="text-ink-secondary">Location</dt>
          <dd>{s.locations.find((l) => l.locationId === p.primaryLocationId)?.name ?? "Another location"}</dd>
        </dl>
      ) : (
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () =>
                api.patients.update(p.patientId, {
                  version: p.version,
                  givenName: form.givenName.trim(),
                  familyName: form.familyName.trim(),
                  dateOfBirth: form.dateOfBirth,
                  sex: form.sex,
                  contact: { address1: orNull(form.address1), address2: orNull(form.address2), city: orNull(form.city), state: orNull(form.state) ? form.state.trim().toUpperCase() : null, postalCode: orNull(form.postalCode), phone: orNull(form.phone), email: orNull(form.email) },
                  asthmaStatus: form.asthma === "unknown" ? null : { asthmatic: form.asthma === "yes", effectiveFrom: form.asthmaFrom || new Date().toISOString().slice(0, 10) },
                }),
              () => setEditing(false),
            );
          }}
        >
          <Field label="First name" required value={form.givenName} onChange={set("givenName")} />
          <Field label="Last name" required value={form.familyName} onChange={set("familyName")} />
          <Field label="Date of birth" type="date" required value={form.dateOfBirth} onChange={set("dateOfBirth")} />
          <Select label="Sex" options={Object.entries(SEX_LABELS).map(([value, label]) => ({ value, label }))} value={form.sex} onChange={set("sex")} />
          <Field label="Address" value={form.address1} onChange={set("address1")} maxLength={120} />
          <Field label="Address line 2" value={form.address2} onChange={set("address2")} maxLength={120} />
          <Field label="City" value={form.city} onChange={set("city")} maxLength={80} />
          <Field label="State" value={form.state} onChange={set("state")} maxLength={2} />
          <Field label="Postal code" value={form.postalCode} onChange={set("postalCode")} maxLength={10} />
          <Field label="Phone" type="tel" value={form.phone} onChange={set("phone")} maxLength={20} />
          <Field label="Email" type="email" value={form.email} onChange={set("email")} maxLength={254} />
          <Select label="Asthma" options={[{ value: "unknown", label: "Not recorded" }, { value: "yes", label: "Asthmatic" }, { value: "no", label: "Not asthmatic" }]} value={form.asthma} onChange={set("asthma")} />
          {form.asthma !== "unknown" && <Field label="Asthma status effective from" type="date" value={form.asthmaFrom} onChange={set("asthmaFrom")} />}
          {note && <Status kind={note.kind} label={note.text} className="sm:col-span-2" />}
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" disabled={busy}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

export function InsuranceCard({ p, onSaved }: { p: PatientDetail; onSaved: () => void }) {
  const s = useSession();
  const api = useClinical();
  const [editing, setEditing] = useState(false);
  const i = p.insurance;
  const [form, setForm] = useState({ carrier: i?.carrier ?? "", memberId: i?.memberId ?? "", groupNumber: i?.groupNumber ?? "", relationship: i?.relationship ?? "", phone: i?.phone ?? "", benefitDate: i?.benefitDate ?? "", hraHsa: i?.hraHsa ?? false, hraHsaBalance: dollars(i?.hraHsaBalanceCents) });
  const { busy, note, run } = useSave(onSaved);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <Card as="section" aria-labelledby="insurance" dense>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 id="insurance" className="text-h3 font-semibold text-anchor">
          Insurance
        </h2>
        {s.can("clinical:write") && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {i ? "Edit" : "Add"}
          </Button>
        )}
      </div>
      {!editing ? (
        i ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-body">
            <dt className="text-ink-secondary">Carrier</dt>
            <dd>{i.carrier ?? "—"}</dd>
            <dt className="text-ink-secondary">Member</dt>
            <dd>{i.memberId ?? "—"}</dd>
            <dt className="text-ink-secondary">Group</dt>
            <dd>{i.groupNumber ?? "—"}</dd>
            <dt className="text-ink-secondary">Relationship</dt>
            <dd>{i.relationship ?? "—"}</dd>
            <dt className="text-ink-secondary">Benefit date</dt>
            <dd>{i.benefitDate ?? "—"}</dd>
            <dt className="text-ink-secondary">HRA/HSA</dt>
            <dd>{i.hraHsa ? `Yes${i.hraHsaBalanceCents !== null ? `, $${dollars(i.hraHsaBalanceCents)}` : ""}` : "No"}</dd>
          </dl>
        ) : (
          <p className="text-ink-secondary">No insurance recorded.</p>
        )
      ) : (
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api.patients.updateInsurance(p.patientId, { version: p.version, carrier: orNull(form.carrier), memberId: orNull(form.memberId), groupNumber: orNull(form.groupNumber), relationship: (form.relationship || null) as (typeof RELATIONSHIPS)[number] | null, phone: orNull(form.phone), benefitDate: form.benefitDate || null, hraHsa: form.hraHsa, hraHsaBalanceCents: form.hraHsa ? cents(form.hraHsaBalance) : null }), () => setEditing(false));
          }}
        >
          <Field label="Carrier" value={form.carrier} onChange={set("carrier")} maxLength={120} />
          <Field label="Member id" value={form.memberId} onChange={set("memberId")} maxLength={60} />
          <Field label="Group number" value={form.groupNumber} onChange={set("groupNumber")} maxLength={60} />
          <Select label="Relationship to subscriber" options={RELATIONSHIPS.map((r) => ({ value: r, label: r[0]!.toUpperCase() + r.slice(1) }))} placeholder="—" value={form.relationship} onChange={set("relationship")} />
          <Field label="Insurance phone" type="tel" value={form.phone} onChange={set("phone")} maxLength={20} />
          <Field label="Benefit date" type="date" value={form.benefitDate} onChange={set("benefitDate")} />
          <label className="flex items-center gap-2 text-body sm:col-span-2">
            <input type="checkbox" className="h-4 w-4 accent-[var(--cta-bg)]" checked={form.hraHsa} onChange={(e) => setForm({ ...form, hraHsa: e.target.checked })} />
            Has an HRA or HSA
          </label>
          {form.hraHsa && <Field label="HRA/HSA balance (dollars)" type="number" step="0.01" min={0} value={form.hraHsaBalance} onChange={set("hraHsaBalance")} />}
          {note && <Status kind={note.kind} label={note.text} className="sm:col-span-2" />}
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" disabled={busy}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

type BenefitRow = { on: boolean; deductible: string; deductibleMet: string; oopMax: string; oopMet: string; coinsurance: string; copay: string; units: string; authRequired: boolean; authAction: string };
const rowOf = (b?: PatientBenefit): BenefitRow => ({ on: !!b, deductible: dollars(b?.deductibleCents), deductibleMet: dollars(b?.deductibleMetCents), oopMax: dollars(b?.outOfPocketMaxCents), oopMet: dollars(b?.outOfPocketMetCents), coinsurance: b?.coinsurancePercent === null || b?.coinsurancePercent === undefined ? "" : String(b.coinsurancePercent), copay: dollars(b?.copayCents), units: b?.unitsCovered === null || b?.unitsCovered === undefined ? "" : String(b.unitsCovered), authRequired: b?.authRequired ?? false, authAction: b?.authAction ?? "" });

/** Stored benefit figures only; the estimator is deferred (Q-57). Money is entered in dollars and kept in integer cents (Q-25). */
export function BenefitsCard({ p, onSaved }: { p: PatientDetail; onSaved: () => void }) {
  const s = useSession();
  const api = useClinical();
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Record<string, BenefitRow>>(() => Object.fromEntries(BENEFIT_KINDS.map((k) => [k, rowOf(p.benefits.find((b) => b.benefitKind === k))])));
  const { busy, note, run } = useSave(onSaved);
  const update = (k: string, patch: Partial<BenefitRow>) => setRows({ ...rows, [k]: { ...rows[k]!, ...patch } });

  return (
    <Card as="section" aria-labelledby="benefits" dense>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 id="benefits" className="text-h3 font-semibold text-anchor">
          Benefits
        </h2>
        {s.can("clinical:write") && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>
      {!editing ? (
        p.benefits.length === 0 ? (
          <p className="text-ink-secondary">No benefit figures recorded. They are shown on the agreements when present; no estimate is computed (Q-57).</p>
        ) : (
          <ul className="flex flex-col gap-2 text-body">
            {p.benefits.map((b) => (
              <li key={b.benefitKind}>
                <span className="font-medium">{BENEFIT_LABELS[b.benefitKind]}</span>: deductible ${dollars(b.deductibleCents) || "—"} (${dollars(b.deductibleMetCents) || "—"} met), out of pocket ${dollars(b.outOfPocketMaxCents) || "—"} (${dollars(b.outOfPocketMetCents) || "—"} met), coinsurance {b.coinsurancePercent ?? "—"}%, copay ${dollars(b.copayCents) || "—"}
                {b.authRequired && ` · authorization required${b.authAction ? `: ${b.authAction}` : ""}`}
              </li>
            ))}
          </ul>
        )
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            const benefits = BENEFIT_KINDS.filter((k) => rows[k]!.on).map((k) => {
              const r = rows[k]!;
              return { benefitKind: k, deductibleCents: cents(r.deductible), deductibleMetCents: cents(r.deductibleMet), outOfPocketMaxCents: cents(r.oopMax), outOfPocketMetCents: cents(r.oopMet), coinsurancePercent: r.coinsurance.trim() === "" ? null : Number(r.coinsurance), copayCents: cents(r.copay), unitsCovered: r.units.trim() === "" ? null : Number(r.units), authRequired: r.authRequired, authAction: orNull(r.authAction) };
            });
            void run(() => api.patients.updateBenefits(p.patientId, { version: p.version, benefits }), () => setEditing(false));
          }}
        >
          {BENEFIT_KINDS.map((k) => {
            const r = rows[k]!;
            return (
              <fieldset key={k} className="rounded-md border border-rule-subtle p-3">
                <legend className="px-1 text-small font-medium text-ink">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="h-4 w-4 accent-[var(--cta-bg)]" checked={r.on} onChange={(e) => update(k, { on: e.target.checked })} />
                    {BENEFIT_LABELS[k]}
                  </label>
                </legend>
                {r.on && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Deductible ($)" type="number" step="0.01" min={0} value={r.deductible} onChange={(e) => update(k, { deductible: e.target.value })} />
                    <Field label="Deductible met ($)" type="number" step="0.01" min={0} value={r.deductibleMet} onChange={(e) => update(k, { deductibleMet: e.target.value })} />
                    <Field label="Out-of-pocket max ($)" type="number" step="0.01" min={0} value={r.oopMax} onChange={(e) => update(k, { oopMax: e.target.value })} />
                    <Field label="Out-of-pocket met ($)" type="number" step="0.01" min={0} value={r.oopMet} onChange={(e) => update(k, { oopMet: e.target.value })} />
                    <Field label="Coinsurance (%)" type="number" step="0.1" min={0} max={100} value={r.coinsurance} onChange={(e) => update(k, { coinsurance: e.target.value })} />
                    <Field label="Copay ($)" type="number" step="0.01" min={0} value={r.copay} onChange={(e) => update(k, { copay: e.target.value })} />
                    <Field label="Units covered" type="number" step={1} min={0} value={r.units} onChange={(e) => update(k, { units: e.target.value })} />
                    <label className="flex items-center gap-2 self-end text-body">
                      <input type="checkbox" className="h-4 w-4 accent-[var(--cta-bg)]" checked={r.authRequired} onChange={(e) => update(k, { authRequired: e.target.checked })} />
                      Authorization required
                    </label>
                    {r.authRequired && <Field label="Authorization action" value={r.authAction} onChange={(e) => update(k, { authAction: e.target.value })} maxLength={200} className="sm:col-span-2" />}
                  </div>
                )}
              </fieldset>
            );
          })}
          {note && <Status kind={note.kind} label={note.text} />}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

/** The patient photo as a document (Q-48): a presigned upload, shown through a short-lived link once the version is verified. */
export function PhotoCard({ p, onSaved }: { p: PatientDetail; onSaved: () => void }) {
  const s = useSession();
  const api = useClinical();
  const input = useRef<HTMLInputElement>(null);
  const doc = useLoad((a) => (p.photoDocumentId ? a.documents.get(p.photoDocumentId) : Promise.resolve(null)), [p.photoDocumentId]);
  const link = useLoad((a) => (doc.data?.currentVersionId ? a.documents.access(doc.data.documentId, doc.data.currentVersionId) : Promise.resolve(null)), [doc.data?.currentVersionId]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "success" | "error" | "info"; text: string } | null>(null);
  const timezone = s.location?.timezone ?? "UTC";

  async function upload(file: File) {
    if (!["image/png", "image/jpeg"].includes(file.type)) return setNote({ kind: "error", text: "Choose a PNG or JPEG image." });
    if (file.size > 2_097_152) return setNote({ kind: "error", text: "The image must be 2 MiB or smaller." });
    setBusy(true);
    setNote(null);
    try {
      const grant = await api.patients.requestPhotoUpload(p.patientId, { contentType: file.type as "image/png" | "image/jpeg", byteLength: file.size });
      const res = await fetch(grant.uploadUrl, { method: "PUT", headers: grant.headers, body: file });
      if (!res.ok) throw new Error(`The upload was refused (${res.status}).`);
      setNote({ kind: "success", text: "Uploaded. The photo appears once the stored file is verified." });
      onSaved();
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  const pending = doc.data?.versions.find((v) => v.status === "queued");
  return (
    <Card as="section" aria-labelledby="photo" dense>
      <h2 id="photo" className="mb-3 text-h3 font-semibold text-anchor">
        Photo
      </h2>
      {link.data ? <img src={link.data.url} alt={`${p.givenName} ${p.familyName}`} className="mb-3 h-40 w-40 rounded-md object-cover" /> : <p className="mb-3 text-ink-secondary">{p.photoDocumentId ? (pending ? `A photo was uploaded ${formatDateTime(pending.createdAt, timezone)} and awaits verification.` : "No verified photo yet.") : "No photo on file."}</p>}
      {note && <Status kind={note.kind} label={note.text} className="mb-3 block" />}
      {s.can("clinical:write") && (
        <label className="flex flex-col gap-2 text-small font-medium text-ink">
          {p.photoDocumentId ? "Replace photo" : "Add photo"} (PNG or JPEG, up to 2 MiB)
          <input ref={input} type="file" accept="image/png,image/jpeg" disabled={busy} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} className="text-body font-normal" />
        </label>
      )}
    </Card>
  );
}
